import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, ShieldCheck, TrendingUp, Search,
  Building2, Landmark, Handshake, Users, Briefcase, BadgeCheck,
} from "lucide-react";
import { Logo } from "@/components/AppShell";
import PricingTable from "@/components/PricingTable";

const audiences = [
  { icon: Building2, label: "Businesses" },
  { icon: TrendingUp, label: "Investors" },
  { icon: Landmark, label: "Lenders" },
  { icon: Briefcase, label: "Suppliers" },
  { icon: Handshake, label: "Contractors" },
  { icon: Users, label: "Service Providers" },
  { icon: BadgeCheck, label: "Nonprofits" },
  { icon: ShieldCheck, label: "Government" },
];

const features = [
  { icon: Sparkles, title: "AI Opportunity Matching", desc: "Our GPT-powered engine scores every potential partner, contract, funding source and customer for strategic fit." },
  { icon: ShieldCheck, title: "Verified Trust Network", desc: "Business verification badges and live trust scores so you always know who you're dealing with." },
  { icon: Search, title: "Smart Discovery", desc: "Find suppliers, vendors, capital and enterprise buyers across every industry from one searchable directory." },
  { icon: TrendingUp, title: "Growth Analytics", desc: "Dashboard insights, opportunity alerts and CRM tools that turn connections into closed deals." },
];

export function LandingNav({ user }) {
  return (
    <header className="glass-nav sticky top-0 z-50 border-b border-slate-200/60">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
          <a href="#features" className="hover:text-[#0A0F1C]">Features</a>
          <a href="#audiences" className="hover:text-[#0A0F1C]">Who it's for</a>
          <Link to="/pricing" className="hover:text-[#0A0F1C]">Pricing</Link>
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <Link to="/dashboard" data-testid="nav-dashboard-cta" className="h-10 px-5 rounded-md bg-[#0A0F1C] text-white text-sm font-medium flex items-center hover:bg-[#1E293B] transition-colors">Dashboard</Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-slate-700 hover:text-[#0A0F1C]">Sign in</Link>
              <Link to="/register" data-testid="nav-register-cta" className="h-10 px-5 rounded-md bg-[#2563EB] text-white text-sm font-medium flex items-center gap-1.5 hover:bg-[#1D4ED8] transition-colors">Get started <ArrowRight size={15} /></Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function Hero({ onStart }) {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#2563EB] bg-[#2563EB]/10 px-3 py-1.5 rounded-full">
            <Sparkles size={13} /> AI-Powered Business Ecosystem
          </span>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#0A0F1C] leading-[1.05]">
            Connecting Opportunities. Building Trust. Accelerating Growth.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-xl">
            DEI TradeMatch AI connects businesses, investors, lenders, suppliers, contractors, nonprofits,
            government agencies and enterprise buyers through one trusted, AI-powered opportunity network.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={onStart} data-testid="hero-cta-primary" className="h-12 px-7 rounded-md bg-[#2563EB] text-white font-medium flex items-center gap-2 hover:bg-[#1D4ED8] transition-colors">
              Start matching free <ArrowRight size={18} />
            </button>
            <Link to="/pricing" data-testid="hero-cta-pricing" className="h-12 px-7 rounded-md border border-slate-300 bg-white text-[#0A0F1C] font-medium flex items-center hover:bg-slate-50 transition-colors">
              View pricing
            </Link>
          </div>
          <p className="mt-4 text-xs text-slate-400">A Debro Enterprise Inc. platform · No credit card required for Free plan</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
          <div className="absolute -inset-4 bg-[#2563EB]/10 rounded-3xl blur-2xl" />
          <img
            src="https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Business team collaborating"
            className="relative rounded-2xl shadow-xl border border-slate-200 w-full object-cover aspect-[4/3]"
          />
        </motion.div>
      </div>
    </section>
  );
}

export function AudienceGrid() {
  return (
    <section id="audiences" className="max-w-7xl mx-auto px-6 py-12">
      <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">One ecosystem for every business relationship</p>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {audiences.map((a, i) => {
          const Icon = a.icon;
          return (
            <motion.div key={a.label}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3.5">
              <Icon size={20} className="text-[#2563EB]" />
              <span className="font-medium text-sm text-[#0A0F1C]">{a.label}</span>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

export function FeaturesSection() {
  return (
    <section id="features" className="max-w-7xl mx-auto px-6 py-16">
      <div className="max-w-2xl">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0F1C]">
          Intelligence that turns connections into growth
        </h2>
        <p className="mt-3 text-slate-600">Everything you need to discover, verify and win the right opportunities.</p>
      </div>
      <div className="mt-10 grid md:grid-cols-2 gap-5">
        {features.map((f, i) => {
          const Icon = f.icon;
          return (
            <motion.div key={f.title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="rounded-xl border border-slate-200 bg-white p-7 hover:shadow-md transition-all duration-200">
              <div className="h-11 w-11 rounded-lg bg-[#0A0F1C] flex items-center justify-center">
                <Icon size={20} className="text-white" />
              </div>
              <h3 className="mt-4 font-heading text-xl font-bold text-[#0A0F1C]">{f.title}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

export function PricingPreview({ plans, cycle, setCycle, onSelect }) {
  return (
    <section className="max-w-7xl mx-auto px-6 py-16">
      <div className="text-center max-w-2xl mx-auto">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0F1C]">Simple, transparent pricing</h2>
        <p className="mt-3 text-slate-600">Start free. Upgrade as you grow.</p>
        <div className="mt-6 inline-flex items-center gap-1 bg-slate-100 rounded-full p-1">
          <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly" className={`px-4 py-1.5 rounded-full text-sm font-medium ${cycle === "monthly" ? "bg-white shadow text-[#0A0F1C]" : "text-slate-500"}`}>Monthly</button>
          <button onClick={() => setCycle("yearly")} data-testid="cycle-yearly" className={`px-4 py-1.5 rounded-full text-sm font-medium ${cycle === "yearly" ? "bg-white shadow text-[#0A0F1C]" : "text-slate-500"}`}>Yearly</button>
        </div>
      </div>
      <div className="mt-10">
        <PricingTable plans={plans} cycle={cycle} onSelect={onSelect} ctaLabel="Get started" />
      </div>
    </section>
  );
}

export function CtaSection({ onStart }) {
  return (
    <section className="max-w-7xl mx-auto px-6 pb-20">
      <div className="relative overflow-hidden rounded-2xl bg-[#0A0F1C] px-8 py-14 text-center">
        <div className="grain absolute inset-0 opacity-50" />
        <h2 className="relative font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">Ready to find your next opportunity?</h2>
        <p className="relative mt-3 text-slate-300 max-w-xl mx-auto">Join the trusted AI-powered ecosystem built for serious business growth.</p>
        <button onClick={onStart} data-testid="cta-bottom" className="relative mt-7 h-12 px-8 rounded-md bg-[#2563EB] text-white font-medium inline-flex items-center gap-2 hover:bg-[#1D4ED8] transition-colors">
          Create your free account <ArrowRight size={18} />
        </button>
      </div>
    </section>
  );
}

export function LandingFooter() {
  return (
    <footer className="border-t border-slate-200 py-8">
      <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <Logo />
        <p className="text-xs text-slate-400">© 2026 Debro Enterprise Inc. All rights reserved. · DEI TradeMatch AI</p>
      </div>
    </footer>
  );
}
